import React, { useState, useEffect, useMemo, memo } from 'react';
import { X, MapPin, Clock, MessageSquare, Search, Navigation, Calendar, AlertCircle, CheckCircle, PlayCircle, Save, RefreshCw } from 'lucide-react';
import axios from 'axios';
import VoiceRecorder from './VoiceRecorder';
import RouteSummary from './RouteSummary';
import OverdueResolutionModal from './OverdueResolutionModal';
import { sanitize } from '../utils/sanitize';

// OverdueCard — dedicated card for the Overdue tab.
// Intentionally separate from the main visit card to avoid coupling overdue-specific
// logic (customer_confirmed nudge, days-overdue calculation) into the shared card path.
const OverdueCard = ({
    item,
    index,
    formatDistance,
    getDaysOverdue,
    onItemClick,
    onOpenOverdueModal,
}) => {
    const daysOverdue = getDaysOverdue(item.scheduled_date);

    return (
        <div
            data-testid="overdue-card"
            data-visit-id={item.id}
            data-entity-global-id={item.entity_global_id}
            className="bg-gray-800 border border-red-800/50 rounded-xl p-3.5 cursor-pointer shadow-md"
            onClick={() => onItemClick && onItemClick(item)}
        >
            {/* Entity name + distance */}
            <div className="flex items-start justify-between mb-1.5">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                        {index + 1}
                    </div>
                    <span className="font-bold text-sm text-gray-100">
                        {sanitize(item.entity_name)}
                    </span>
                </div>
                {item.distance !== undefined && (
                    <span className="text-xs text-blue-400 font-semibold flex-shrink-0">
                        {formatDistance(item.distance)}
                    </span>
                )}
            </div>

            {/* Days overdue (red) + scheduled date + bumped count (gray) */}
            <div className="flex flex-wrap gap-1.5 mb-2">
                {daysOverdue > 0 && (
                    <span className="bg-red-900/40 text-red-300 rounded-full text-[11px] px-2 py-0.5 font-semibold">
                        {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue
                    </span>
                )}
                {item.scheduled_date && (
                    <span className="bg-gray-700 text-gray-300 rounded-full text-[11px] px-2 py-0.5">
                        {new Date(item.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                )}
                {item.rescheduled_count > 0 && (
                    <span className="bg-gray-700 text-gray-400 rounded-full text-[11px] px-2 py-0.5">
                        Bumped {item.rescheduled_count}&times;
                    </span>
                )}
                {item.customer_confirmed && (
                    <span className="bg-yellow-900/40 text-yellow-300 rounded-full text-[11px] px-2 py-0.5 font-semibold">
                        Confirmed appt
                    </span>
                )}
            </div>

            {/* customer_confirmed nudge banner — only when this was a booked appointment */}
            {item.customer_confirmed === true && (
                <div
                    className="bg-yellow-900/30 border border-yellow-700/50 rounded-md px-2.5 py-2 text-xs text-yellow-300 mb-2.5 leading-snug"
                    onClick={(e) => e.stopPropagation()}
                >
                    This was a confirmed appointment. The customer may still be expecting you.
                </div>
            )}

            {/* CTA buttons — stop propagation so card click (open detail) doesn't fire */}
            <div
                className="flex gap-1.5 mt-1.5"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Mark as Completed — Scenario 1 */}
                <button
                    onClick={() => onOpenOverdueModal(item, 'complete')}
                    data-testid="overdue-action-complete"
                    className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 bg-green-700 hover:bg-green-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                    style={{ minHeight: '44px' }}
                >
                    <CheckCircle size={13} />
                    Mark Completed
                </button>

                {/* Didn't Happen — Scenario 2a or 2b depending on customer_confirmed */}
                <button
                    onClick={() => onOpenOverdueModal(
                        item,
                        item.customer_confirmed ? 'resolve-scheduled' : 'resolve-adhoc'
                    )}
                    data-testid="overdue-action-didnt-happen"
                    className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 bg-orange-700 hover:bg-orange-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                    style={{ minHeight: '44px' }}
                >
                    <X size={13} />
                    Didn't Happen
                </button>
            </div>
        </div>
    );
};

const TodaysVisits = ({ visitList, overdueList, onRemove, onClear, onSync, onClose, onVoiceUploadSuccess, onItemClick, onCheckIn, onCompleteVisit, onFilterChange, viewMode, onViewModeChange, undoableActions, onUndo, onOptimizeRoute, isOptimizing, optimizedRoute, showRouteOnMap, onToggleRouteOnMap, onReschedule, overdueCount, parentFilterMode, onLeadStatusChange, visitsLoading, panelMode = false, hideInternalFilters = false, geofenceActive = false }) => {
    const [feedbackData, setFeedbackData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [visitNotes, setVisitNotes] = useState({}); // Track notes by visit_id
    const [expandedNotes, setExpandedNotes] = useState({}); // Track which notes sections are expanded
    const [saveStatus, setSaveStatus] = useState({}); // Track save status by visit_id ('saving', 'saved', 'error')
    const [filterMode, setFilterMode] = useState('today'); // Strict 3-filter contract: 'today' | 'overdue' | 'scheduled'
    const [undoCountdowns, setUndoCountdowns] = useState({}); // Track countdown timers for undo (30s window)
    const [rescheduleVisitId, setRescheduleVisitId] = useState(null); // Which visit is being rescheduled
    const [rescheduleDate, setRescheduleDate] = useState(''); // Date picker value for rescheduling
    const [showClearConfirm, setShowClearConfirm] = useState(false); // Confirmation dialog for clear
    const [skipVisitId, setSkipVisitId] = useState(null); // Which visit is being skipped
    const [skipReason, setSkipReason] = useState(''); // Selected skip reason
    const [skipStep, setSkipStep] = useState('reason'); // 'reason' | 'comm'
    const [cancelVisitId, setCancelVisitId] = useState(null); // Which visit is being cancelled
    const [cancelReason, setCancelReason] = useState(''); // Selected cancel reason
    const [cancelStep, setCancelStep] = useState('reason'); // 'reason' | 'comm'
    const [completeError, setCompleteError] = useState({}); // Inline error by visit_id for completion validation
    // Overdue resolution modal state — controls which scenario modal is open and for which visit
    const [overdueModal, setOverdueModal] = useState({
        open: false,
        scenario: null,  // 'complete' | 'resolve-adhoc' | 'resolve-scheduled' | 'bulk-absent'
        visit: null,
        absentDate: null,
        absentVisitCount: 0,
    });

    // Sync local filterMode when parent changes it programmatically (e.g., after accepting from briefing)
    // filterMode intentionally excluded to avoid infinite loop (parent sets → effect fires → sets again)
    useEffect(() => {
        if (parentFilterMode && parentFilterMode !== filterMode) {
            setFilterMode(parentFilterMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentFilterMode]);

    // Get user's current location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.warn("Location access denied:", error);
                    // Continue without location - will just show unsorted list
                }
            );
        }
    }, []);

    // Calculate distance between two points (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // Derived: sort by proximity and filter by search term.
    // useMemo avoids the extra render cycle that the old useEffect+setState pattern caused
    // (effect fires after paint → setState → second render). Now it computes synchronously
    // during render, only when visitList, userLocation, or searchTerm change.
    const sortedVisits = useMemo(() => {
        if (!visitList || visitList.length === 0) return [];

        let processed = [...visitList];

        // Add distance if user location available
        if (userLocation) {
            processed = processed.map(item => ({
                ...item,
                distance: calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    item.entity_latitude,
                    item.entity_longitude
                )
            }));
            // Sort by distance (nearest first)
            processed.sort((a, b) => a.distance - b.distance);
        }

        // Apply search filter
        if (searchTerm) {
            processed = processed.filter(item =>
                item.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.entity_address?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return processed;
    // calculateDistance is defined inline in the component body and is stable (no deps),
    // so it intentionally does not need to be in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitList, userLocation, searchTerm]);

    // Derived: sort overdueList by proximity — same logic as sortedVisits.
    // Rendered only when filterMode === 'overdue' (dedicated overdue tab).
    const sortedOverdueList = useMemo(() => {
        if (!overdueList || overdueList.length === 0) return [];

        let processed = [...overdueList];

        if (userLocation) {
            processed = processed.map(item => ({
                ...item,
                distance: calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    item.entity_latitude,
                    item.entity_longitude
                )
            }));
            processed.sort((a, b) => a.distance - b.distance);
        }

        if (searchTerm) {
            processed = processed.filter(item =>
                item.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.entity_address?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return processed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overdueList, userLocation, searchTerm]);

    // Initialize notes from visitList
    useEffect(() => {
        if (visitList && visitList.length > 0) {
            const notes = {};
            visitList.forEach(visit => {
                if (visit.id && visit.notes !== undefined) {
                    notes[visit.id] = visit.notes || '';
                }
            });
            setVisitNotes(notes);
        }
    }, [visitList]);

    // Manage countdown timers for undoable actions (30-second window)
    useEffect(() => {
        if (!undoableActions || undoableActions.size === 0) {
            setUndoCountdowns({});
            return;
        }

        // Update countdown every second
        const interval = setInterval(() => {
            const newCountdowns = {};
            undoableActions.forEach((action, visitId) => {
                const elapsed = Date.now() - action.timestamp;
                const remaining = Math.max(0, 30 - Math.floor(elapsed / 1000));
                if (remaining > 0) {
                    newCountdowns[visitId] = remaining;
                }
            });
            setUndoCountdowns(newCountdowns);
        }, 1000);

        return () => clearInterval(interval);
    }, [undoableActions]);

    useEffect(() => {
        if (onSync) {
            // This is just to satisfy the linter if onSync is used in an effect
            // But actually we use it in handleSaveRoute in App.js
        }
    }, [onSync]);

    // Save notes to backend
    const handleSaveNotes = async (visitId, notes) => {
        try {
            setSaveStatus(prev => ({ ...prev, [visitId]: 'saving' }));

            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

            // Use axios which has the Authorization header set globally by App.js
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                notes: notes
            });

            setSaveStatus(prev => ({ ...prev, [visitId]: 'saved' }));
            console.log(`✅ Notes saved for visit ${visitId}`);

            // Clear saved status after 2 seconds
            setTimeout(() => {
                setSaveStatus(prev => ({ ...prev, [visitId]: null }));
            }, 2000);
        } catch (error) {
            console.error('❌ Failed to save notes:', error);
            if (error.response?.status === 401) {
                console.error('Authentication error - token may be expired');
            }
            setSaveStatus(prev => ({ ...prev, [visitId]: 'error' }));

            // Clear error status after 3 seconds
            setTimeout(() => {
                setSaveStatus(prev => ({ ...prev, [visitId]: null }));
            }, 3000);
        }
    };

    // Handle notes change (without auto-save)
    const handleNotesChange = (visitId, newNotes) => {
        setVisitNotes(prev => ({ ...prev, [visitId]: newNotes }));
    };

    // Handle skip visit — step 1: reason chosen → step 2: comm prompt → submit
    const handleSkipReasonNext = () => {
        if (!skipReason) return;
        setSkipStep('comm');
    };

    const handleSkipSubmit = async (visitId) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
            const visit = visitList.find(v => v.id === visitId);
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                visit_state: 'skipped',
                skip_reason: skipReason
            });
            setSkipVisitId(null);
            setSkipReason('');
            setSkipStep('reason');
            // Revert lead pin/badge back to 'new'
            if (visit?.entity_global_id?.startsWith('lead_') && onLeadStatusChange) {
                onLeadStatusChange(visit.entity_global_id, 'new');
            }
            if (onSync) onSync();
        } catch (error) {
            console.error('Failed to skip visit:', error);
        }
    };

    // Handle cancel visit — step 1: reason chosen → step 2: comm prompt → submit
    const handleCancelReasonNext = () => {
        if (!cancelReason) return;
        setCancelStep('comm');
    };

    const handleCancelSubmit = async (visitId) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
            const visit = visitList.find(v => v.id === visitId);
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                visit_state: 'cancelled',
                skip_reason: cancelReason
            });
            setCancelVisitId(null);
            setCancelReason('');
            setCancelStep('reason');
            // Revert lead pin/badge back to 'new'
            if (visit?.entity_global_id?.startsWith('lead_') && onLeadStatusChange) {
                onLeadStatusChange(visit.entity_global_id, 'new');
            }
            if (onSync) onSync();
        } catch (error) {
            console.error('Failed to cancel visit:', error);
        }
    };

    // Handle complete visit with notes/voice validation
    const handleCompleteWithValidation = (visitId, itemGlobalId) => {
        const visit = visitList.find(v => v.id === visitId);
        const hasNotes = visitNotes[visitId] || visit?.notes;
        const hasVoice = !!(feedbackData[itemGlobalId]);
        if (!hasNotes && !hasVoice) {
            setExpandedNotes(prev => ({ ...prev, [visitId]: true }));
            setCompleteError(prev => ({ ...prev, [visitId]: true }));
            return;
        }
        setCompleteError(prev => ({ ...prev, [visitId]: false }));
        if (onCompleteVisit) onCompleteVisit(visitId);
    };

    // Skip reason options
    const SKIP_REASONS = [
        'Store closed',
        'No decision maker available',
        'Bad weather / road conditions',
        'Running late / time constraint',
        'Higher priority visit',
        'Other'
    ];

    // Cancel reason options
    const CANCEL_REASONS = [
        'Customer requested cancellation',
        'Store permanently closed',
        'Duplicate visit',
        'No longer relevant',
        'Manager instruction',
        'Other'
    ];

    // Format distance for display
    const formatDistance = (km) => {
        if (km < 1) return `${Math.round(km * 1000)}m`;
        return `${km.toFixed(1)}km`;
    };

    // Calculate days since item was added to list
    const getDaysSinceAdded = (claimedAt) => {
        if (!claimedAt) return null;
        const added = new Date(claimedAt);
        const now = new Date();
        const diffTime = Math.abs(now - added);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Check if visit is stale (>7 days old)
    const isStale = (claimedAt) => {
        const days = getDaysSinceAdded(claimedAt);
        return days !== null && days > 7;
    };

    // Calculate days overdue from scheduled_date
    const getDaysOverdue = (scheduledDate) => {
        if (!scheduledDate) return 0;
        const scheduled = new Date(scheduledDate + 'T00:00:00'); // Parse as local date
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const diffTime = now - scheduled;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Get overdue severity badge styling
    const getOverdueBadge = (daysOverdue) => {
        if (daysOverdue > 14) {
            return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: `${daysOverdue}d overdue` };
        } else if (daysOverdue > 7) {
            return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', label: `${daysOverdue}d overdue` };
        } else if (daysOverdue >= 1) {
            return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: `${daysOverdue}d overdue` };
        }
        return null;
    };

    // Handle reschedule submission
    const handleRescheduleSubmit = async (visitId) => {
        if (!rescheduleDate || !onReschedule) return;
        await onReschedule(visitId, rescheduleDate);
        setRescheduleVisitId(null);
        setRescheduleDate('');
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return "Tomorrow";
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    // Get visit state badge styling
    const getVisitStateBadge = (visitState) => {
        const states = {
            'planned': {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                label: '📅 Planned',
                icon: Calendar
            },
            'checked_in': {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: '✓ Checked In',
                icon: PlayCircle
            },
            'completed': {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: '✅ Completed',
                icon: CheckCircle
            },
            'skipped': {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: '⊘ Skipped',
                icon: X
            },
            'cancelled': {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Cancelled',
                icon: AlertCircle
            }
        };
        return states[visitState] || states['planned'];
    };

    // Open navigation app
    const handleNavigate = (item) => {
        const lat = item.entity_latitude;
        const lng = item.entity_longitude;

        // Detect platform and open appropriate maps app
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const url = isIOS
            ? `maps://maps.apple.com/?daddr=${lat},${lng}`
            : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

        window.open(url, '_blank');
    };

    const handleVoiceUpload = async (itemGlobalId, visitId, data) => {
        // Store feedback data by global_id
        setFeedbackData(prev => ({
            ...prev,
            [itemGlobalId]: data.extraction
        }));

        // Auto-save a note so the backend notes-required check passes on completion
        if (visitId && !visitNotes[visitId]) {
            const voiceNote = 'Voice memo recorded';
            try {
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
                await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, { notes: voiceNote });
                setVisitNotes(prev => ({ ...prev, [visitId]: voiceNote }));
            } catch (err) {
                console.error('Failed to auto-save voice note:', err);
            }
        }

        // Notify parent component if callback provided
        if (onVoiceUploadSuccess) {
            onVoiceUploadSuccess(data);
        }
    };

    // Open the overdue resolution modal for a specific visit + scenario
    const openOverdueModal = (visit, scenario) => {
        setOverdueModal({ open: true, scenario, visit, absentDate: null, absentVisitCount: 0 });
    };

    // Open the bulk-absent modal for a shared date group
    const openBulkAbsentModal = (date, count) => {
        setOverdueModal({ open: true, scenario: 'bulk-absent', visit: null, absentDate: date, absentVisitCount: count });
    };

    // Called by OverdueResolutionModal after a successful API call.
    // refresh=true means a new visit was created (reschedule) — trigger a full sync.
    // refresh=false means the visit was completed/cancelled — also sync to remove it.
    const handleOverdueSuccess = ({ refresh }) => {
        setOverdueModal({ open: false, scenario: null, visit: null, absentDate: null, absentVisitCount: 0 });
        // Always re-fetch — overdue list needs to shrink
        if (onSync) onSync();
    };

    // Handle filter mode change
    const handleFilterChange = (newFilterMode) => {
        setFilterMode(newFilterMode);
        // Notify parent component to refetch visits with new filter
        if (onFilterChange) {
            onFilterChange(newFilterMode);
        }
    };

    // Handle view mode change
    const handleViewModeChange = (newViewMode) => {
        // Notify parent component to refetch visits with new view mode
        if (onViewModeChange) {
            onViewModeChange(newViewMode);
        }
    };

    // Full view - Show card even if visitList is empty to allow filter switching
    return (
        <div
            data-testid="visits-panel"
            data-view-mode={viewMode}
            data-filter-mode={filterMode}
            className={panelMode
            ? "h-full w-full bg-gray-900 flex flex-col overflow-hidden"
            : "fixed top-20 right-4 md:top-44 md:right-6 bg-gray-900 rounded-xl shadow-2xl w-[90vw] md:w-80 max-h-[calc(100vh-180px)] overflow-hidden z-30 border border-gray-700 flex flex-col"
        }>
            {/* Header — blue/purple brand gradient, matches AI Briefing + NavRail */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 text-white overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 ring-2 ring-white/30 flex items-center justify-center">
                            <MapPin className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-lg tracking-tight truncate">My Visits</h3>
                                {geofenceActive && (
                                    <span className="flex items-center gap-1 text-[10px] font-semibold bg-white/20 text-white px-1.5 py-0.5 rounded-full">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse inline-block" />
                                        GPS
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 text-indigo-100 text-xs mt-0.5 font-medium">
                                <Clock className="w-3 h-3" />
                                {(() => {
                                    const count = (filterMode === 'overdue' && viewMode === 'route') ? sortedOverdueList.length : sortedVisits.length;
                                    return <span>{count} {count === 1 ? 'visit' : 'visits'} {searchTerm ? 'found' : 'in this view'}</span>;
                                })()}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="visits-panel-close"
                        className="flex-shrink-0 text-white/80 hover:text-white hover:bg-white/15 p-2 rounded-lg transition-colors"
                        title="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* View Mode Toggle - Two-Tier System: Tier 1.
                Hidden when the host view (SideBar) is providing these controls. */}
            {!hideInternalFilters && (
            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-700 bg-gray-800">
                <div className="flex gap-2">
                    <button
                        onClick={() => handleViewModeChange('route')}
                        data-testid="visit-view-route"
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'route'
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                            : 'bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-600'
                            }`}
                    >
                        <span>🚀 My Route</span>
                        <span className={`block text-[10px] font-normal mt-0.5 ${viewMode === 'route' ? 'text-blue-100' : 'text-gray-500'}`}>Active work queue</span>
                    </button>
                    <button
                        onClick={() => handleViewModeChange('history')}
                        data-testid="visit-view-history"
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'history'
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                            : 'bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-600'
                            }`}
                    >
                        <span>📜 History</span>
                        <span className={`block text-[10px] font-normal mt-0.5 ${viewMode === 'history' ? 'text-blue-100' : 'text-gray-500'}`}>Past visits</span>
                    </button>
                </div>
            </div>
            )}

            {/* Filter Tabs — only shown in route view; history is a flat all-visits list.
                Also hidden when the host view (SideBar) is providing these controls. */}
            {viewMode === 'route' && !hideInternalFilters && (
                <div className="flex-shrink-0 px-4 pt-4 pb-5 border-b border-gray-700 bg-gray-800 overflow-x-auto">
                    <div className="flex gap-2 min-w-max">
                        <button
                            onClick={() => handleFilterChange('today')}
                            data-testid="visit-filter-today"
                            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${filterMode === 'today'
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                                : 'bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-600'
                                }`}
                        >
                            📍 Today{sortedVisits.length > 0 ? ` (${sortedVisits.length})` : ''}
                        </button>
                        <button
                            onClick={() => handleFilterChange('overdue')}
                            data-testid="visit-filter-overdue"
                            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${filterMode === 'overdue'
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                                : 'bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-600'
                                }`}
                        >
                            ⚠️ Overdue{overdueCount > 0 ? ` (${overdueCount})` : ''}
                        </button>
                        <button
                            onClick={() => handleFilterChange('scheduled')}
                            data-testid="visit-filter-scheduled"
                            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${filterMode === 'scheduled'
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                                : 'bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-600'
                                }`}
                        >
                            📅 Scheduled
                        </button>
                    </div>
                </div>
            )}

            {/* Optimize Route Button — only in route view with 2+ visits */}
            {viewMode === 'route' && sortedVisits.length >= 2 && (
                <div className="flex-shrink-0 px-3 pt-2 pb-1 border-b border-gray-700 bg-gray-800">
                    <button
                        onClick={onOptimizeRoute}
                        disabled={isOptimizing}
                        data-testid="optimize-route-btn"
                        data-optimizing={isOptimizing ? 'true' : 'false'}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            isOptimizing
                                ? 'bg-gray-700 text-gray-500 cursor-wait'
                                : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
                        }`}
                    >
                        <Navigation className="w-4 h-4" />
                        {isOptimizing ? 'Optimizing...' : 'Optimize My Route'}
                    </button>
                    <RouteSummary
                        optimizedRoute={optimizedRoute}
                        showRouteOnMap={showRouteOnMap}
                        onToggleRouteOnMap={onToggleRouteOnMap}
                    />
                </div>
            )}

            {/* Search Bar */}
            <div className="flex-shrink-0 p-3 border-b border-gray-700 bg-gray-800">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Search by name or address..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="visit-search"
                        className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Visit List - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-900">
                {/* Loading spinner — shown while a tab-switch fetch is in flight.
                    Replaces the stale previous-tab items so the user gets instant feedback. */}
                {visitsLoading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
                        <div className="w-8 h-8 border-[3px] border-gray-700 border-t-blue-500 rounded-full animate-spin" />
                        <span className="text-sm font-medium">Loading…</span>
                    </div>
                ) : /* Overdue tab: render dedicated overdue list with bulk-absent banners.
                    Guard on viewMode === 'route' — filterMode can remain 'overdue' even after
                    switching to history (it is local state, not reset on view-mode change).
                    Without this guard the overdue branch steals the render from the history list. */
                filterMode === 'overdue' && viewMode === 'route' ? (
                    sortedOverdueList.length === 0 ? (
                        <div className="flex flex-col items-center justify-center text-center py-10 px-5 bg-gray-700/30 rounded-xl text-gray-500">
                            <div className="text-3xl mb-2">✅</div>
                            <p className="font-semibold text-gray-400 mb-1">No overdue visits</p>
                            <p className="text-sm">You're all caught up!</p>
                        </div>
                    ) : (() => {
                        // Group visits by scheduled_date to detect "absent day" clusters (2+ on same date).
                        // We iterate in display order (sortedOverdueList) and inject a banner before each
                        // qualifying date group.
                        const dateGroups = {}; // date → [visit, ...]
                        sortedOverdueList.forEach(v => {
                            const d = v.scheduled_date || '__no_date__';
                            if (!dateGroups[d]) dateGroups[d] = [];
                            dateGroups[d].push(v);
                        });

                        // Track which banners we've already rendered so we only show one per date
                        const renderedBanners = new Set();
                        const nodes = [];

                        sortedOverdueList.forEach((item, index) => {
                            const d = item.scheduled_date;
                            // Inject bulk-absent banner once per qualifying date (2+ visits, valid date)
                            if (d && dateGroups[d].length >= 2 && !renderedBanners.has(d)) {
                                renderedBanners.add(d);
                                const displayD = new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
                                const count = dateGroups[d].length;
                                nodes.push(
                                    <div
                                        key={`bulk-banner-${d}`}
                                        className="bg-yellow-900/30 border border-yellow-700/50 rounded-xl px-3.5 py-3 flex items-center justify-between gap-2.5 flex-wrap"
                                    >
                                        <span className="text-sm font-semibold text-yellow-300">
                                            Were you absent on {displayD}?
                                        </span>
                                        <button
                                            onClick={() => openBulkAbsentModal(d, count)}
                                            className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white border-none rounded-lg text-xs font-bold cursor-pointer transition-colors whitespace-nowrap"
                                            style={{ minHeight: '36px' }}
                                        >
                                            Yes, handle all ({count})
                                        </button>
                                    </div>
                                );
                            }

                            nodes.push(
                                <OverdueCard
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    formatDistance={formatDistance}
                                    getDaysOverdue={getDaysOverdue}
                                    onItemClick={onItemClick}
                                    onOpenOverdueModal={openOverdueModal}
                                />
                            );
                        });

                        return nodes;
                    })()
                ) : sortedVisits.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-700/30 rounded-xl">
                        <div className="text-6xl mb-4">📭</div>
                        <h4 className="text-lg font-semibold text-gray-300 mb-2">No visits in this view</h4>
                        <p className="text-sm text-gray-500 text-center">
                            {filterMode === 'today' && "No visits scheduled for today. Check 'Overdue' or 'Scheduled' tabs."}
                            {filterMode === 'scheduled' && "No future visits scheduled yet."}
                        </p>
                    </div>
                ) : (
                    sortedVisits.map((item, index) => (
                        <div
                            key={item.id}
                            data-testid="visit-card"
                            data-visit-id={item.id}
                            data-entity-global-id={item.entity_global_id}
                            data-visit-state={item.visit_state}
                            className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:shadow-lg hover:border-purple-600/50 transition-all cursor-pointer"
                            onClick={() => onItemClick && onItemClick(item)}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                        {index + 1}
                                    </div>
                                    <h4 data-testid="visit-card-name" className="font-semibold text-gray-100 text-sm">{sanitize(item.entity_name)}</h4>
                                </div>
                                {viewMode === 'route' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(item.entity_global_id);
                                        }}
                                        data-testid="visit-card-remove"
                                        className="text-gray-400 hover:text-red-600 transition-colors p-1 z-10"
                                        title="Remove from route"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <div className="space-y-1 text-xs mb-3">
                                {/* Distance Badge (if available) */}
                                {item.distance !== undefined && (
                                    <div className="flex items-center gap-2 text-blue-400 font-semibold">
                                        <Navigation className="w-4 h-4" />
                                        <span>{formatDistance(item.distance)} away</span>
                                    </div>
                                )}

                                {/* Temporal Info: Added date, scheduled date, stale warning */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {item.claimed_at && (
                                        <div className="flex items-center gap-1 text-gray-400">
                                            <Clock className="w-3 h-3" />
                                            <span>Added {getDaysSinceAdded(item.claimed_at) === 0 ? 'today' : `${getDaysSinceAdded(item.claimed_at)}d ago`}</span>
                                        </div>
                                    )}

                                    {item.scheduled_date && (
                                        <div className="flex items-center gap-1 text-purple-400 font-medium">
                                            <Calendar className="w-3 h-3" />
                                            <span>{formatDate(item.scheduled_date)}</span>
                                        </div>
                                    )}

                                    {/* Overdue Badge with severity colors — dark variants */}
                                    {item.visit_state === 'planned' && getDaysOverdue(item.scheduled_date) >= 1 && getOverdueBadge(getDaysOverdue(item.scheduled_date)) && (() => {
                                        const daysOv = getDaysOverdue(item.scheduled_date);
                                        const darkBg = daysOv > 14 ? 'bg-red-900/40 text-red-300' : daysOv > 7 ? 'bg-orange-900/40 text-orange-300' : 'bg-yellow-900/40 text-yellow-300';
                                        const badge = getOverdueBadge(daysOv);
                                        return (
                                            <div className={`flex items-center gap-1 ${darkBg} px-2 py-0.5 rounded-full font-semibold`}>
                                                <AlertCircle className="w-3 h-3" />
                                                <span>{badge.label}</span>
                                            </div>
                                        );
                                    })()}

                                    {/* Rescheduled Count Badge */}
                                    {item.rescheduled_count > 0 && (
                                        <div className="flex items-center gap-1 bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded-full font-medium">
                                            <RefreshCw className="w-3 h-3" />
                                            <span>Rescheduled x{item.rescheduled_count}</span>
                                        </div>
                                    )}

                                    {isStale(item.claimed_at) && getDaysOverdue(item.scheduled_date) < 1 && (
                                        <div className="flex items-center gap-1 bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded-full font-semibold">
                                            <AlertCircle className="w-3 h-3" />
                                            <span>Stale</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Priority:</span>
                                    <span className="text-gray-200 font-medium">
                                        {item.priority === 'high' ? '🔥🔥🔥' :
                                            item.priority === 'medium' ? '⚡⚡' :
                                                '❄️'} {item.priority || 'Low'}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`px-2 py-0.5 rounded-full font-semibold text-xs ${item.entity_status === 'at_risk' ? 'bg-orange-900/40 text-orange-300' :
                                        item.entity_status === 'churned' ? 'bg-red-900/40 text-red-300' :
                                            item.entity_status === 'new' ? 'bg-blue-900/40 text-blue-300' :
                                                item.entity_status === 'planned' ? 'bg-purple-900/40 text-purple-300' :
                                                    item.entity_status === 'contacted' ? 'bg-sky-900/40 text-sky-300' :
                                                        item.entity_status === 'won' ? 'bg-green-900/40 text-green-300' :
                                                            item.entity_status === 'lost' ? 'bg-gray-700 text-gray-400' :
                                                                item.entity_status === 'active' ? 'bg-emerald-900/40 text-emerald-300' :
                                                                    item.entity_status === 'inactive' ? 'bg-gray-700 text-gray-400' :
                                                                        'bg-blue-900/40 text-blue-300'
                                        }`}>
                                        {(item.entity_status || '').toUpperCase().replace('_', ' ')}
                                    </span>
                                    {/* Visit State Badge — only show for non-planned states (checked_in, etc.)
                                        'planned' is implied by context and shown by entity_status, so suppress it to avoid
                                        "NEW 📅 Planned" / "WON 📅 Planned" double-badge noise */}
                                    {item.visit_state && item.visit_state !== 'planned' && (() => {
                                        const vsb = getVisitStateBadge(item.visit_state);
                                        // Map light state badge classes to dark equivalents
                                        const darkMap = {
                                            'bg-yellow-100 text-yellow-700': 'bg-yellow-900/40 text-yellow-300',
                                            'bg-blue-100 text-blue-700': 'bg-blue-900/40 text-blue-300',
                                            'bg-green-100 text-green-700': 'bg-green-900/40 text-green-300',
                                            'bg-gray-100 text-gray-700': 'bg-gray-700 text-gray-400',
                                            'bg-red-100 text-red-700': 'bg-red-900/40 text-red-300',
                                        };
                                        const darkClass = darkMap[`${vsb.bg} ${vsb.text}`] || 'bg-gray-700 text-gray-400';
                                        return (
                                            <span className={`px-2 py-0.5 rounded-full font-semibold text-xs ${darkClass}`}>
                                                {vsb.label}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Actions Section */}
                            <div className="border-t border-gray-700 pt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                                {/* Visit State Actions */}
                                {item.visit_state === 'planned' && onCheckIn && (
                                    <button
                                        onClick={() => onCheckIn(item.id)}
                                        data-testid="visit-action-checkin"
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all text-xs font-semibold shadow-md hover:shadow-lg"
                                    >
                                        <PlayCircle className="w-4 h-4" />
                                        Check In
                                    </button>
                                )}

                                {/* Reschedule Button — for overdue planned visits */}
                                {item.visit_state === 'planned' && getDaysOverdue(item.scheduled_date) >= 1 && onReschedule && (
                                    rescheduleVisitId === item.id ? (
                                        <div className="flex items-center gap-2" data-testid="reschedule-flow">
                                            <input
                                                type="date"
                                                value={rescheduleDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setRescheduleDate(e.target.value)}
                                                data-testid="reschedule-date-input"
                                                className="flex-1 px-2 py-2 bg-gray-700 border border-gray-600 text-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            />
                                            <button
                                                onClick={() => handleRescheduleSubmit(item.id)}
                                                disabled={!rescheduleDate}
                                                data-testid="reschedule-save"
                                                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => { setRescheduleVisitId(null); setRescheduleDate(''); }}
                                                data-testid="reschedule-cancel"
                                                className="px-2 py-2 text-gray-500 hover:text-gray-300 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setRescheduleVisitId(item.id)}
                                            data-testid="visit-action-reschedule"
                                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 rounded-lg transition-colors text-xs font-semibold border border-purple-800"
                                        >
                                            <Calendar className="w-4 h-4" />
                                            Reschedule Visit
                                        </button>
                                    )
                                )}

                                {item.visit_state === 'checked_in' && onCompleteVisit && (
                                    <div className="space-y-2">
                                        {completeError[item.id] && (
                                            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="visit-complete-error">
                                                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                                <span className="text-xs text-red-400">Add notes or record a voice memo before completing</span>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => handleCompleteWithValidation(item.id, item.entity_global_id)}
                                            data-testid="visit-action-complete"
                                            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg transition-all text-xs font-semibold shadow-md hover:shadow-lg"
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            Complete Visit
                                        </button>
                                    </div>
                                )}

                                {/* Skip Button — for planned visits */}
                                {item.visit_state === 'planned' && (
                                    skipVisitId === item.id ? (
                                        <div className="space-y-2 bg-gray-800 p-3 rounded-lg border border-gray-600" data-testid="skip-flow">
                                            {skipStep === 'reason' ? (
                                                <>
                                                    <p className="text-xs font-semibold text-gray-300">Why are you skipping?</p>
                                                    <select
                                                        value={skipReason}
                                                        onChange={(e) => setSkipReason(e.target.value)}
                                                        data-testid="skip-reason-select"
                                                        className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 text-gray-200 rounded-lg focus:ring-2 focus:ring-gray-400"
                                                    >
                                                        <option value="">Select a reason...</option>
                                                        {SKIP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                                    </select>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={handleSkipReasonNext}
                                                            disabled={!skipReason}
                                                            data-testid="skip-next"
                                                            className="flex-1 px-3 py-1.5 bg-gray-500 hover:bg-gray-400 text-white text-xs rounded-lg font-semibold disabled:opacity-40"
                                                        >
                                                            Next →
                                                        </button>
                                                        <button
                                                            onClick={() => { setSkipVisitId(null); setSkipReason(''); setSkipStep('reason'); }}
                                                            data-testid="skip-back"
                                                            className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs"
                                                        >
                                                            Back
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-xs font-semibold text-gray-300">Let the customer know?</p>
                                                    <p className="text-xs text-gray-400">Optionally reach out before moving on.</p>
                                                    <div className="flex gap-2">
                                                        <a
                                                            href={`tel:${item.entity_phone || ''}`}
                                                            className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg font-semibold text-center"
                                                            onClick={() => handleSkipSubmit(item.id)}
                                                            data-testid="skip-call"
                                                        >
                                                            📞 Call
                                                        </a>
                                                        <button
                                                            onClick={() => handleSkipSubmit(item.id)}
                                                            data-testid="skip-confirm"
                                                            className="flex-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded-lg font-semibold"
                                                        >
                                                            Skip anyway
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { setSkipVisitId(item.id); setSkipStep('reason'); }}
                                            data-testid="visit-action-skip"
                                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-xs font-semibold border border-gray-600"
                                        >
                                            <X className="w-4 h-4" />
                                            Skip Visit
                                        </button>
                                    )
                                )}

                                {/* Cancel Button — for planned or checked-in visits */}
                                {(item.visit_state === 'planned' || item.visit_state === 'checked_in') && (
                                    cancelVisitId === item.id ? (
                                        <div className="space-y-2 bg-gray-800 p-3 rounded-lg border border-red-800" data-testid="cancel-flow">
                                            {cancelStep === 'reason' ? (
                                                <>
                                                    <p className="text-xs font-semibold text-red-400">Reason for cancellation?</p>
                                                    <select
                                                        value={cancelReason}
                                                        onChange={(e) => setCancelReason(e.target.value)}
                                                        data-testid="cancel-reason-select"
                                                        className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-red-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-red-500"
                                                    >
                                                        <option value="">Select a reason...</option>
                                                        {CANCEL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                                    </select>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={handleCancelReasonNext}
                                                            disabled={!cancelReason}
                                                            data-testid="cancel-next"
                                                            className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg font-semibold disabled:opacity-40"
                                                        >
                                                            Next →
                                                        </button>
                                                        <button
                                                            onClick={() => { setCancelVisitId(null); setCancelReason(''); setCancelStep('reason'); }}
                                                            data-testid="cancel-back"
                                                            className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs"
                                                        >
                                                            Back
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-xs font-semibold text-red-400">Let the customer know?</p>
                                                    <p className="text-xs text-gray-400">It's good practice to notify before cancelling.</p>
                                                    <div className="flex gap-2">
                                                        <a
                                                            href={`tel:${item.entity_phone || ''}`}
                                                            className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg font-semibold text-center"
                                                            onClick={() => handleCancelSubmit(item.id)}
                                                            data-testid="cancel-call"
                                                        >
                                                            📞 Call
                                                        </a>
                                                        <button
                                                            onClick={() => handleCancelSubmit(item.id)}
                                                            data-testid="cancel-confirm"
                                                            className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg font-semibold"
                                                        >
                                                            Cancel anyway
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { setCancelVisitId(item.id); setCancelStep('reason'); }}
                                            data-testid="visit-action-cancel"
                                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors text-xs font-semibold border border-red-800"
                                        >
                                            <AlertCircle className="w-4 h-4" />
                                            Cancel Visit
                                        </button>
                                    )
                                )}

                                {/* Undo Button (30-second window) */}
                                {undoCountdowns[item.id] && undoCountdowns[item.id] > 0 && (
                                    <button
                                        onClick={() => onUndo(item.id)}
                                        data-testid="visit-action-undo"
                                        data-undo-seconds-remaining={undoCountdowns[item.id]}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-900/30 hover:bg-orange-900/50 text-orange-300 rounded-lg transition-all text-xs font-semibold border border-orange-800/50"
                                    >
                                        <span className="font-mono" data-testid="undo-countdown">{undoCountdowns[item.id]}s</span>
                                        <span>Undo</span>
                                    </button>
                                )}

                                {/* Navigate Button */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleNavigate(item)}
                                        data-testid="visit-action-navigate"
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-900/30 hover:bg-blue-900/50 text-blue-300 rounded-lg transition-colors text-xs font-semibold border border-blue-800/50"
                                    >
                                        <Navigation className="w-4 h-4" />
                                        Navigate
                                    </button>
                                </div>

                                {/* Visit Notes Section */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setExpandedNotes(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                                        data-testid="visit-notes-toggle"
                                        className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors"
                                    >
                                        <MessageSquare className="w-4 h-4" />
                                        <span>{expandedNotes[item.id] ? 'Hide Notes' : 'Add Notes'}</span>
                                    </button>

                                    {expandedNotes[item.id] && (
                                        <div className="space-y-2">
                                            <textarea
                                                value={visitNotes[item.id] || ''}
                                                onChange={(e) => handleNotesChange(item.id, e.target.value)}
                                                placeholder="Add notes about this visit..."
                                                data-testid="visit-notes-textarea"
                                                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                                                rows={3}
                                            />
                                            <button
                                                onClick={() => handleSaveNotes(item.id, visitNotes[item.id] || '')}
                                                disabled={saveStatus[item.id] === 'saving'}
                                                data-testid="visit-notes-save"
                                                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${saveStatus[item.id] === 'saving'
                                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                                    : saveStatus[item.id] === 'saved'
                                                        ? 'bg-green-600 text-white'
                                                        : saveStatus[item.id] === 'error'
                                                            ? 'bg-red-600 text-white'
                                                            : 'bg-emerald-600 text-white hover:bg-emerald-500'
                                                    }`}
                                            >
                                                <Save className="w-3 h-3" />
                                                <span>
                                                    {saveStatus[item.id] === 'saving'
                                                        ? 'Saving...'
                                                        : saveStatus[item.id] === 'saved'
                                                            ? 'Saved!'
                                                            : saveStatus[item.id] === 'error'
                                                                ? 'Error - Try Again'
                                                                : 'Save Notes'}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Voice Recorder */}
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Record Visit</span>
                                    <VoiceRecorder
                                        customerId={item.entity_type === 'customer' ? parseInt(item.entity_global_id.split('_')[1]) : undefined}
                                        leadId={item.entity_type === 'lead' ? parseInt(item.entity_global_id.split('_')[1]) : undefined}
                                        onUploadSuccess={(data) => handleVoiceUpload(item.entity_global_id, item.id, data)}
                                    />
                                </div>

                                {/* Display feedback if available */}
                                {feedbackData[item.entity_global_id] && (
                                    <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-2 space-y-1">
                                        <div className="flex items-start gap-2">
                                            <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 space-y-1">
                                                <p className="text-xs text-gray-300 leading-relaxed">
                                                    {sanitize(feedbackData[item.entity_global_id].summary)}
                                                </p>
                                                <div className="flex flex-wrap gap-2 text-xs">
                                                    <span className={`px-2 py-0.5 rounded-full font-semibold ${feedbackData[item.entity_global_id].sentiment === 'positive' ? 'bg-green-900/40 text-green-300' :
                                                        feedbackData[item.entity_global_id].sentiment === 'negative' ? 'bg-red-900/40 text-red-300' :
                                                            'bg-gray-700 text-gray-400'
                                                        }`}>
                                                        {feedbackData[item.entity_global_id].sentiment === 'positive' ? '😊' :
                                                            feedbackData[item.entity_global_id].sentiment === 'negative' ? '😞' : '😐'}
                                                        {feedbackData[item.entity_global_id].sentiment}
                                                    </span>
                                                    {feedbackData[item.entity_global_id].order_amount > 0 && (
                                                        <span className="px-2 py-0.5 rounded-full font-semibold bg-emerald-900/40 text-emerald-300">
                                                            💰 ¥{feedbackData[item.entity_global_id].order_amount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer — Clear visible visits only for route view (and overdue tab) */}
            {viewMode === 'route' && (filterMode === 'overdue' ? sortedOverdueList.length > 0 : sortedVisits.length > 0) && (
                <div className="flex-shrink-0 p-4 bg-gray-800 border-t border-gray-700">
                    <button
                        onClick={() => setShowClearConfirm(true)}
                        data-testid="clear-visits-btn"
                        className="w-full px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/50 rounded-lg font-semibold transition-colors text-sm"
                    >
                        {filterMode === 'today' ? "Clear Today's Visits" :
                         filterMode === 'overdue' ? 'Clear Overdue Visits' :
                         filterMode === 'scheduled' ? 'Clear Scheduled Visits' :
                         'Clear Visits'}
                    </button>

                    {showClearConfirm && (
                        <div className="mt-2 bg-red-900/30 border border-red-800/50 rounded-lg p-3" data-testid="clear-visits-confirm">
                            <p className="text-sm text-red-300 font-medium mb-2">
                                Clear {sortedVisits.length} {filterMode === 'today' ? "today's" : filterMode} visits? This cannot be undone.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { onClear(filterMode); setShowClearConfirm(false); }}
                                    data-testid="clear-visits-yes"
                                    className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-sm rounded-lg font-semibold transition-colors"
                                >
                                    Yes, Clear
                                </button>
                                <button
                                    onClick={() => setShowClearConfirm(false)}
                                    data-testid="clear-visits-no"
                                    className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg font-semibold border border-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* OverdueResolutionModal — renders as last child so it layers over the panel content */}
            <OverdueResolutionModal
                isOpen={overdueModal.open}
                onClose={() => setOverdueModal(s => ({ ...s, open: false }))}
                scenario={overdueModal.scenario}
                visit={overdueModal.visit}
                absentDate={overdueModal.absentDate}
                absentVisitCount={overdueModal.absentVisitCount}
                onSuccess={handleOverdueSuccess}
            />
        </div>
    );
};

// Shallow memo on the key props that drive re-renders. Callback props (onRemove, onClose, etc.)
// will still break memoization if App.js doesn't useCallback them, but we gate on the
// data-driven props so at minimum identical-data renders are skipped.
export default memo(TodaysVisits, (prev, next) => {
    return (
        prev.visitList === next.visitList &&
        prev.overdueList === next.overdueList &&
        prev.visitsLoading === next.visitsLoading &&
        prev.viewMode === next.viewMode &&
        prev.parentFilterMode === next.parentFilterMode &&
        prev.overdueCount === next.overdueCount &&
        prev.isOptimizing === next.isOptimizing &&
        prev.optimizedRoute === next.optimizedRoute &&
        prev.showRouteOnMap === next.showRouteOnMap &&
        prev.undoableActions === next.undoableActions &&
        prev.onRemove === next.onRemove &&
        prev.onClear === next.onClear &&
        prev.onSync === next.onSync &&
        prev.onClose === next.onClose &&
        prev.onVoiceUploadSuccess === next.onVoiceUploadSuccess &&
        prev.onItemClick === next.onItemClick &&
        prev.onCheckIn === next.onCheckIn &&
        prev.onCompleteVisit === next.onCompleteVisit &&
        prev.onFilterChange === next.onFilterChange &&
        prev.onViewModeChange === next.onViewModeChange &&
        prev.onUndo === next.onUndo &&
        prev.onOptimizeRoute === next.onOptimizeRoute &&
        prev.onToggleRouteOnMap === next.onToggleRouteOnMap &&
        prev.onReschedule === next.onReschedule &&
        prev.onLeadStatusChange === next.onLeadStatusChange
    );
});
